import { Button, Table, TableCell, TablePager, TableRow } from '@fukuri-admins/ui/common';
import { StatusBadge } from './StatusBadge';
import styles from './styles.module.scss';

/** アップロード状況テーブルのソートキー */
type UploadStatusSortKey = 'status' | 'receptNo' | 'scheduledAt' | 'registeredCount';

/**
 * アップロード状況テーブルの行データ型
 *
 * 表示整形済みの値を受け取る（converter で API 型から変換される）
 */
type UploadStatusRow = {
  /** 行の一意キー */
  id: number;
  /**
   * ステータス（API 値。legacy の StatusBadge と同じ値セット）
   *
   * - invalid_csv: 失敗
   * - registered: 予約中
   * - csv_uploaded: 準備中
   * - processing: 処理中
   * - completed_with_errors: 一部失敗
   * - completed: 完了
   * - cancelled: 取り消し
   */
  status:
    | 'invalid_csv'
    | 'registered'
    | 'csv_uploaded'
    | 'processing'
    | 'completed_with_errors'
    | 'completed'
    | 'cancelled';
  /** 受付番号 */
  receptNo: string;
  /** エラー内容（エラーがない場合は undefined） */
  errorContent?: string;
  /** 実行日時（表示整形済み） */
  scheduledAt: string;
  /** 登録人数（target_user_count の API 値をそのまま表示） */
  registeredCount: number;
};

type Props = {
  /** テーブルに表示するアップロード状況行データ */
  rows: UploadStatusRow[];
  /**
   * ソートキー押下時のコールバック
   *
   * @param key ソート対象のキー
   */
  onSort: (key: UploadStatusSortKey) => void;
  /** ページャー props（currentPage / totalPage / itemCount / totalCount / onPageChange） */
  pagerProps: React.ComponentProps<typeof TablePager>;
  /**
   * 行の「予約を取り消す」ボタン押下時のコールバック
   *
   * @param id 操作対象の一括登録リクエスト ID
   */
  onCancelReservation: (id: number) => void;
};

/**
 * アップロード状況タブ用テーブル（テーブル本体 + ページャー）
 *
 * rows が 0 件のときは Empty 表示のみ返す（テーブル・ページャーは非表示）
 */
export const UploadStatusTable: React.FC<Props> = ({
  rows,
  onSort,
  pagerProps,
  onCancelReservation,
}) =>
  rows.length === 0 ? (
    <div className={styles.empty}>
      <span>アップロードされたデータはまだありません</span>
    </div>
  ) : (
    <>
      <Table
        scrollable
        headers={[
          {
            label: 'ステータス',
            className: styles.uploadStatusCol,
            sortable: true,
            onSort: () => onSort('status'),
          },
          {
            label: '受付番号',
            className: styles.receptNoCol,
            sortable: true,
            onSort: () => onSort('receptNo'),
          },
          { label: 'エラー内容', className: styles.errorContentCol },
          {
            label: '登録人数',
            className: styles.registeredCountCol,
            rightTextAlign: true,
            sortable: true,
            onSort: () => onSort('registeredCount'),
          },
          {
            label: '実行日時',
            className: styles.scheduledAtCol,
            sortable: true,
            onSort: () => onSort('scheduledAt'),
          },
          { label: '操作', className: styles.actionCol },
        ]}
      >
        {rows.map(row => {
          const hasError = row.status === 'invalid_csv' || row.status === 'completed_with_errors';
          return (
            <TableRow key={row.id}>
              <TableCell align='center'>
                <StatusBadge status={row.status} />
              </TableCell>
              <TableCell>{row.receptNo}</TableCell>
              <TableCell className={hasError ? styles.errorContent : undefined}>
                {row.errorContent ?? '-'}
              </TableCell>
              <TableCell align='right'>{row.registeredCount}</TableCell>
              <TableCell>{row.scheduledAt}</TableCell>
              <TableCell>
                {row.status === 'registered' && (
                  <Button
                    variant='ghostDestructive'
                    size='sm'
                    onClick={() => onCancelReservation(row.id)}
                  >
                    予約を取り消す
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </Table>
      <TablePager {...pagerProps} />
    </>
  );
